import React, { useEffect, useState } from "react";

// third party
import moment from "moment";
import { Transition, config, animated } from "react-spring/renderprops";

// component
import { SelectFilterCustom } from "../_commons/SelectFilterCustom";
import Image from "../_commons/Image";

// utils
import { fixedToTwoDecimal, formatDate, printCurrency, trackEvent } from "../../atlas-utils";

// services
import NotificationServices from "../../services/NotificationService";

// constants
import { CATALOGUE_PLATFORMS_LOGO, FOODTYPE_MAPS, TRACKING_EVENT_NAMES } from "../../client-config";
import { clientMenu } from "../../client-menu";
import { GET_LOCATION_MENUS_LIST } from "../../graphql/locations";
import history from "../../history";
import { FormSidebar } from "../_commons/FormSidebar";
import Placeholder from "../_commons/Placeholder";
import { GET_PARENT_SECTIONS_LIST } from "../../graphql/menus";
import FoodTypeIndicator from "../_commons/FoodTypeIndicator";
import { connect } from "react-redux";
import ListShimmer from "../_commons/ListShimmer";
import { Button } from "../_commons/Button";

const Menu = ({ handleBrandLocation, handleBrandsLabelOption, isMultibrandEnabled, data }) => {
	const [locationMenusListData, setLocationMenusListData] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [menuView, setMenuView] = useState({ isOpen: false, menuId: null, name: "", description: "" });

	const handleMenuViewClose = () => {
		setMenuView({
			isOpen: false,
			menuId: null,
			name: "",
			description: ""
		});
	};

	const fetchLocationMenusList = async () => {
		try {
			setIsLoading(true);
			const variables = {
				id: parseInt(data?.id),
				brandId: isMultibrandEnabled ? String(data?.selectedBrandLocation?.brand?.id) : ""
			};
			const menusListResp = await clientMenu.query({
				query: GET_LOCATION_MENUS_LIST,
				variables,
				fetchPolicy: "no-cache"
			});

			if (menusListResp?.data?.location) {
				setLocationMenusListData(menusListResp.data.location);
			} else {
				NotificationServices.pushNotification({
					message: "Failed to fetch menus list",
					timeout: 5000,
					type: "error",
					isClosable: true,
					theme: "dark"
				});
			}
			setIsLoading(false);
		} catch (e) {
			setIsLoading(false);
			console.log(e);
			NotificationServices.pushNotification({
				message: "Failed to fetch menus list",
				timeout: 5000,
				type: "error",
				isClosable: true,
				theme: "dark"
			});
		}
	};

	useEffect(() => {
		fetchLocationMenusList();
		trackEvent(TRACKING_EVENT_NAMES.LOCATION_DETAILS_MENU_TAB_VIEWS, {});
	}, [data?.selectedBrandLocation]);

	if (isLoading) {
		return (
			<div className="menus-association-container">
				<div className="P(10px)">
					<div className="shimmer H(60px) Mb(10px)" />
					<div className="shimmer H(60px) Mb(10px)" />
				</div>
			</div>
		);
	}

	if (
		locationMenusListData?.publishedMenus?.length === 0 &&
		locationMenusListData?.scheduledPublishes?.length === 0
	) {
		return (
			<div className="menus-association-container">
				{isMultibrandEnabled && (
					<div className="brand-selector-container">
						<SelectFilterCustom
							options={data?.brandLocations || []}
							field="selectedBrandLocation"
							currValue={data?.selectedBrandLocation || null}
							setFilter={(f, value) => handleBrandLocation(value)}
							labelKey={"brandName"}
							valueKey={"brandId"}
							isSearchable={false}
							customLabel={true}
							customOptions={true}
							isClearable={false}
							renderLabel={handleBrandsLabelOption}
							renderOptions={handleBrandsLabelOption}
							placeholder="Select brand"
						/>
					</div>
				)}
				<Placeholder
					placeholderText="No menus are associated to this location!"
					placeholderImageUrl="/assets/empty_states/graphics-empty-catalogue.svg"
					size="medium"
				/>
			</div>
		);
	}

	return (
		<div className="menus-association-container">
			{isMultibrandEnabled && (
				<div className="brand-selector-container">
					<SelectFilterCustom
						options={data?.brandLocations || []}
						field="selectedBrandLocation"
						currValue={data?.selectedBrandLocation || null}
						setFilter={(f, value) => handleBrandLocation(value)}
						labelKey={"brandName"}
						valueKey={"brandId"}
						isSearchable={false}
						customLabel={true}
						customOptions={true}
						isClearable={false}
						renderLabel={handleBrandsLabelOption}
						renderOptions={handleBrandsLabelOption}
						placeholder="Select brand"
					/>
				</div>
			)}
			{!!locationMenusListData?.publishedMenus?.length && (
				<Header
					header="Current Menus"
					subHeader="The menus which are published and active on your brand location"
				/>
			)}
			{(locationMenusListData?.publishedMenus ?? []).map((publishedMenu, i) => (
				<MenuInfo key={i} setMenuView={setMenuView} {...publishedMenu} />
			))}
			{!!locationMenusListData?.scheduledPublishes?.length && (
				<Header
					header="Scheduled Menus"
					subHeader="The menus which are scheduled to replace your current menus"
				/>
			)}
			{(locationMenusListData?.scheduledPublishes ?? []).map((publishedMenu, i) => (
				<MenuInfo key={i} setMenuView={setMenuView} {...publishedMenu} />
			))}
			<MenuView
				menuId={menuView?.menuId}
				isOpen={menuView?.isOpen}
				name={menuView?.name}
				description={menuView?.description}
				close={handleMenuViewClose}
			/>
		</div>
	);
};

export default Menu;

const Header = ({ header, subHeader }) => (
	<div className="header-container">
		<div className="header">{header}</div>
		<div className="sub-header">{subHeader}</div>
	</div>
);

const MenuInfo = ({ menuDetails, platforms = [], publishedAt, publishedBy, setMenuView }) => (
	<div className="menu-info-container">
		<div className="menu-image-container"></div>
		<div className="menu-metadata-container">
			<div className="title-desc-actions-container">
				<div className="title-desc-container">
					<div className="header">
						{menuDetails?.name}
						{/* <span className='version'>v8</span> */}
					</div>
					<div className="sub-header">{menuDetails?.description}</div>
				</div>
				<div className="actions-container">
					<Image
						src="/assets/icons/icon-eye.svg"
						alt="view menu"
						title="view menu"
						onClick={() =>
							setMenuView({
								isOpen: true,
								menuId: menuDetails?.menuId,
								name: menuDetails?.name,
								description: menuDetails?.description
							})
						}
					/>
					<Image
						src="/assets/icons/icon-edit-dark--light.svg"
						alt="edit menu"
						title="edit menu"
						onClick={() => history.push(`/menus/edit/${menuDetails?.menuId}`)}
					/>
				</div>
			</div>
			<div className="item-sections-count-container">
				<Image src="/assets/icons/icon-fork-spoon-mini.svg" className="icon" />
				{menuDetails?.metadata?.categoryCount} sections, {menuDetails?.metadata?.itemCount} items
			</div>
			<div className="platforms-publish-details-container">
				<div className="platforms-container">
					{platforms.map((plf, i) => (
						<Image
							key={i}
							src={CATALOGUE_PLATFORMS_LOGO[plf.toLowerCase()]}
							alt={plf}
							className="platform-image"
						/>
					))}
				</div>
				<div className="publish-info">
					<span className="status">Published</span> &nbsp; on{" "}
					{formatDate(moment(publishedAt), "DD MMM YYYY, HH:MM A")} by {publishedBy}
				</div>
			</div>
		</div>
	</div>
);

const MenuView = ({ menuId = "", isOpen, close = () => {}, name, description }) => {
	const [sectionsData, setSectionsData] = useState([]);
	const [selectedParentSection, setSelectedParentSection] = useState();
	const [isLoading, setIsLoading] = useState(false);

	const updateSelectedSection = (id) => {
		setSelectedParentSection(id);
	};

	const fetchMenuSections = async () => {
		if (!isOpen || !menuId) {
			return;
		}
		setIsLoading(true);
		try {
			const variables = {
				menuId,
				offset: 0,
				limit: 100,
				searchKeyword: ""
			};
			const respParentSectionsDetails = await clientMenu.query({
				query: GET_PARENT_SECTIONS_LIST,
				variables,
				fetchPolicy: "no-cache"
			});

			if (respParentSectionsDetails?.data?.categories) {
				const categories = respParentSectionsDetails?.data?.categories;
				let subCategories = {};
				(categories?.objects ?? []).forEach((parentCategory) => {
					parentCategory.subCategories.forEach((subCategory) => {
						subCategories[subCategory?.id] = true;
					});
				});
				const restructuredParentSections = (categories?.objects ?? []).filter(
					(section) => !subCategories[section?.id]
				);
				setSectionsData(respParentSectionsDetails?.data?.categories);
				if (restructuredParentSections?.length > 0) {
					updateSelectedSection(restructuredParentSections?.[0]?.id);
				}
			} else {
				NotificationServices.pushNotification({
					message: "Failed to get menu section details",
					timeout: 5000,
					type: "error",
					isClosable: true,
					theme: "dark"
				});
			}
			setIsLoading(false);
		} catch (e) {
			console.log(e);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchMenuSections();
	}, [menuId, isOpen]);

	let subCategories = {};
	(sectionsData?.objects ?? []).forEach((parentCategory) => {
		parentCategory.subCategories.forEach((subCategory) => {
			subCategories[subCategory?.id] = true;
		});
	});

	const restructuredParentSections = (sectionsData?.objects ?? []).filter((section) => !subCategories[section?.id]);
	const selectedParentSectionData = sectionsData?.objects?.find((section) => section?.id === selectedParentSection);

	const renderMenuDetails = () => {
		if (!!sectionsData?.length) {
			return <Placeholder />;
		}

		return (
			<div className="categories-container">
				<div className="parent-categories-list">
					<div className="parent-sections-list-container">
						{restructuredParentSections.map((val) => (
							<React.Fragment>
								<div
									key={val?.id}
									className={`parent-category ${
										val?.id === selectedParentSection
											? `parent-category--selected ${
													!!val?.subCategories?.length
														? "parent-category--selected-includes-subsection"
														: ""
											  }`
											: ""
									}`}
									onClick={() => updateSelectedSection(val?.id)}
								>
									<span className="parent-category--name">{val?.name}</span>
								</div>
								<Transition
									native
									from={{ height: 0, opacity: 0 }}
									enter={{ height: "auto", opacity: 1 }}
									leave={{ height: 0, opacity: 0, delay: 0 }}
									items={selectedParentSection === val?.id && !!val?.subCategories?.length}
									config={{ ...config.stiff, ...{ duration: 100 } }}
								>
									{(isOpen) =>
										isOpen &&
										((props) => (
											<animated.div className={"sub-sections-list"} style={props}>
												{(val?.subCategories ?? []).map((subCategory, i) => (
													<div className="sub-section" key={subCategory?.id}>
														{subCategory?.name}
														<span
															className={`line--vertical ${
																i === val?.subCategories.length - 1 ? "half" : ""
															}`}
														></span>
													</div>
												))}
											</animated.div>
										))
									}
								</Transition>
							</React.Fragment>
						))}
					</div>
				</div>
				<div
					className={`selected-section-content--container ${
						!!selectedParentSectionData?.items.length || !!selectedParentSectionData?.subCategories.length
							? "selected-section-content--container-flex-start"
							: ""
					}`}
				>
					{selectedParentSectionData?.items.length === 0 &&
					selectedParentSectionData?.subCategories.length === 0 ? (
						<Placeholder
							size="small"
							placeholderText="No items or sub-sections found!"
							placeholderImageUrl="/assets/empty_states/graphics-empty-catalogue.svg"
						/>
					) : !!selectedParentSectionData?.items?.length ? (
						<>
							<div className="header-container">
								<div className="title">{selectedParentSectionData?.name}</div>
								<div className="sub-title">{selectedParentSectionData?.description}</div>
							</div>
							{
								<div className="preview-items-container">
									{selectedParentSectionData?.items?.length === 0 ? (
										<Placeholder placeholderText="No items found!" />
									) : (
										selectedParentSectionData.items.map((item) => (
											<PreviewItem
												key={item?.id}
												imageUrl={item?.imageUrl}
												title={item?.name}
												foodType={item?.type}
												actualPrice={item?.price}
											/>
										))
									)}
								</div>
							}
						</>
					) : (
						<>
							{selectedParentSectionData?.subCategories.map((subCategory) => {
								const subCategoryData = sectionsData?.objects?.find(
									(section) => section?.id === subCategory?.id
								);
								return (
									<>
										<div className="header-container">
											<div className="title">{selectedParentSectionData?.name}</div>
											<div className="sub-title">
												{selectedParentSectionData?.description || "--"}
											</div>
										</div>
										<div className="preview-items-container">
											{subCategoryData?.items?.length === 0 ? (
												<Placeholder placeholderText="No items found!" />
											) : (
												subCategoryData.items.map((item) => (
													<PreviewItem
														key={item?.id}
														imageUrl={item?.imageUrl}
														title={item?.name}
														foodType={item?.type}
														actualPrice={item?.price}
													/>
												))
											)}
										</div>
									</>
								);
							})}
						</>
					)}
				</div>
			</div>
		);
	};

	if (isLoading) {
		return (
			<FormSidebar isOpen={isOpen} isNested close={close} hideActions title={name} subTitle={description}>
				<div className="menu-view-container">
					<ListShimmer />
				</div>
			</FormSidebar>
		);
	}

	return (
		<FormSidebar
			isOpen={isOpen}
			isNested
			close={close}
			hideActions
			title={name}
			subTitle={description}
			headerRight={<Button onClick={() => history.push(`/menus/edit/${menuId}`)}>Configure</Button>}
		>
			<div className="menu-view-container">{renderMenuDetails()}</div>
		</FormSidebar>
	);
};
const PreviewItem = connect((store) => ({
	biz: store.login.loggedInbizDetail
}))(({
	foodType,
	title,
	desc,
	contents,
	imageUrl,
	actualPrice,
	markupPrice,
	isCustomisable,
	showHideCustomisableItemDrawer,
	biz
}) => {
	return (
		<div className="preview-item">
			<div className="item-info">
				<div className="foodtype-title">
					<FoodTypeIndicator foodType={FOODTYPE_MAPS[foodType]} />
					<div className="food-title">{title}</div>
				</div>
				<div className="item-description">{desc}</div>
				<div className="prices-container">
					{markupPrice && <div className="markup-price">{markupPrice}</div>}
					<div className="actual-price">
						{printCurrency(biz.currencySymbol)}
						{fixedToTwoDecimal(actualPrice)}
					</div>
				</div>
				{/* <div className='food-contents'>
					Contents: &nbsp;
					{
						contents.map((content, i) => <span className='content'>{content}{i !== (contents.length - 1) ? ', ' : ''}</span>)
					}
				</div> */}
			</div>
			<div className="item-actions">
				<Image
					src={imageUrl || "/assets/empty-dish.jpg"}
					alt={title}
					className="item-image"
					errorOverrideImage="/assets/empty-dish.jpg"
				/>
			</div>
		</div>
	);
});
